import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {Section} from 'constants/NavConstants';
import {SECTIONS} from 'constants/NavConstants';
import * as NavConstants from 'constants/NavConstants';
import FileStates from 'FileStates';
import {ColumnAccessorKey} from 'nav/expanded/ListingRendererConstants';
import {getSelectedPageSize} from 'nav/sidepanel/PageSizeUtil';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import {getStore} from 'redux-stores/Store';
import {getEmptyFilterErrors} from 'search/filters/ValidateFilters';
import {
  Filter,
  FilterKey,
  keyToEmptyFilter,
  SearchParams,
  SearchType,
  Sort,
  SortKey,
} from 'search/SearchConstants';
import {getAppliedSortsFromUserPreference} from 'search/SearchPreferences';
import {refreshListing, updateParamsAndSearch} from 'search/SearchUIReduxActions';
import {getStudyableModelTypes} from 'studies/studyable/StudyableUtils';
import StudyListingTabState from 'studies/workspace/tabs/StudyListingTabState';
import {ScrollableTableUISelector} from 'ui-library/scrollable/reduceScrollableTableUI';
import {getExpandedSearchTableKey, TABLE_KEYS} from 'ui-library/scrollable/ScrollableTableConstants';
import {hiddenColumnsUpdated} from 'ui-library/scrollable/ScrollableTableUIReduxActions';
import isModelTypeEnabledForListing from 'util/isModelTypeEnabledForListing';

export function getStudyItemListingSearchSessionId(studyId: string): string {
  return `studyItemsListing:${studyId}`;
}

/**
 * Helper function to initialize a search for items in a given study. This must be called before we mount
 * the initial listing component, because withSearching's defaultParameters doesn't have access to props,
 * and so cannot get the studyId.
 *
 * This function always resets the params - future updates could read the previous study search's filters
 * and merge those with the initial params, but we'd need to decide if the session ID would be per-study
 * or global across all open studies.
 */
export function initializeSearchForItemsInStudy(studyId: string): void {
  getStore().dispatch(
    updateParamsAndSearch(
      getStudyItemListingSearchSessionId(studyId),
      SearchType.STUDY_ITEMS,
      {
        offset: 0,
        limit: getSelectedPageSize(),
        query: '',
        sorts: getAppliedSortsFromUserPreference(Section.SEARCH),
        filters: [
          {
            ...keyToEmptyFilter(FilterKey.IN_STUDY),
            value: [studyId],
          },
          {
            ...keyToEmptyFilter(FilterKey.TYPES),
            value: getStudyableModelTypes().filter((type) => isModelTypeEnabledForListing(type)),
          },
        ],
      },
      [null, null]
    )
  );
}

export function getDefaultStudiesSearchParams(): SearchParams {
  return {
    filters: [{...keyToEmptyFilter(FilterKey.TYPES), value: [ModelTypeConstants.STUDY]}],
    offset: 0,
    limit: getSelectedPageSize(),
    query: '',
    sorts: getAppliedSortsFromUserPreference(NavConstants.Section.STUDIES),
  };
}

export function viewRecentlyCreatedStudies(): void {
  const tableKey = getExpandedSearchTableKey(NavConstants.Section.STUDIES);
  getStore().dispatch(
    hiddenColumnsUpdated(
      tableKey,
      new ScrollableTableUISelector(getStore().getState())
        .getHiddenColumnKeysForTable(tableKey, [], TABLE_KEYS.EXPANDED_PROJECT_FILE_TABLE)
        .filter((key: string) => key !== ColumnAccessorKey.CREATED_AT)
    )
  );
  getStore().dispatch(
    updateParamsAndSearch(
      SECTIONS.STUDIES,
      SearchType.STUDIES,
      {
        ...getDefaultStudiesSearchParams(),
        sorts: [{key: SortKey.SORT_CREATED_AT_KEY, reverse: true}],
      },
      getEmptyFilterErrors(getDefaultStudiesSearchParams().filters)
    )
  );
  getStore().dispatch(refreshListing(SECTIONS.STUDIES));
  SidepanelActions.expandSection(SECTIONS.STUDIES);
}

export function switchStudyItemFilter(
  studyId: string,
  additionalFilters: Array<Filter>,
  sorts?: Array<Sort>
): void {
  const inStudyKey = keyToEmptyFilter(FilterKey.IN_STUDY);

  const filters: Array<Filter> = [
    {
      ...inStudyKey,
      value: [studyId],
    },
    ...additionalFilters.filter((filter) => filter.key !== inStudyKey.key),
  ];

  getStore().dispatch(
    updateParamsAndSearch(
      getStudyItemListingSearchSessionId(studyId),
      SearchType.STUDY_ITEMS,
      {
        offset: 0,
        limit: getSelectedPageSize(),
        query: '',
        sorts: sorts ?? getAppliedSortsFromUserPreference(Section.SEARCH),
        filters,
      },
      getEmptyFilterErrors(filters)
    )
  );
}

export async function switchToStudyItemsTab(
  studyId: string,
  additionalFilters?: Array<Filter>,
  sorts?: Array<Sort>
): Promise<void> {
  if (additionalFilters && additionalFilters.length > 0) {
    switchStudyItemFilter(studyId, additionalFilters, sorts);
  }
  const studyFile = await FileStates.get().openFolderItem(studyId, {open: true});
  if (!studyFile) {
    // The user may have aborted opening the file.
    return;
  }
  const existingTab = studyFile.findTab(StudyListingTabState);
  assert(existingTab != null, 'Expected study to have a StudyListingTabState', {studyId});
  existingTab.set({active: true});
}
